/**
 * EnvironmentalMeasurement Service
 * API integration for environmental measurements (#351)
 *
 * @version 2026.1.0
 * @description Serviço de integração com a API de medições ambientais
 */
import apiClient from './apiClient';
import {
  EnvironmentalMeasurement,
  EnvironmentalMeasurementApiPayload,
  EnvironmentalMeasurementFilters,
  EnvironmentalMeasurementFormData,
  EnvironmentalMeasurementListResponse,
} from '../types/environmentalMeasurement';

// Re-export types for convenience
export * from '../types/environmentalMeasurement';

export type EnvironmentalMeasurementListParams = EnvironmentalMeasurementFilters;

/**
 * Campo de texto vazio é a ausência do dado, não uma string em branco — e campo que o
 * formulário nem chegou a entregar é a mesma ausência, pelo mesmo caminho.
 */
const orNull = (value?: string | null): string | null => (value?.trim() ? value.trim() : null);

/** Quantidade vazia é ausência; `Number('')` é 0, que o servidor recusaria como quantidade. */
const numberOrNull = (value?: string | null): number | null => (value?.trim() ? Number(value) : null);

class EnvironmentalMeasurementService {
  private baseUrl = '/environmental-measurements';

  /**
   * Monta a query string compartilhada pela listagem e pela exportação —
   * exportar a lista inteira quando o usuário filtrou é o defeito clássico aqui.
   */
  private buildFilterParams(filters: EnvironmentalMeasurementFilters): URLSearchParams {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', String(filters.companyId));
    if (filters.departmentId) queryParams.append('departmentId', String(filters.departmentId));
    if (filters.occupationId) queryParams.append('occupationId', String(filters.occupationId));
    if (filters.riskId) queryParams.append('riskId', String(filters.riskId));
    if (filters.measurementEquipmentId) {
      queryParams.append('measurementEquipmentId', String(filters.measurementEquipmentId));
    }
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.exceedsLimit !== undefined) {
      queryParams.append('exceedsLimit', String(filters.exceedsLimit));
    }
    if (filters.hazardousFraming !== undefined) {
      queryParams.append('hazardousFraming', String(filters.hazardousFraming));
    }
    if (filters.unhealthyFraming !== undefined) {
      queryParams.append('unhealthyFraming', String(filters.unhealthyFraming));
    }
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    return queryParams;
  }

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(
    formData: EnvironmentalMeasurementFormData,
    includeRiskApplication: boolean,
  ): EnvironmentalMeasurementApiPayload {
    const payload: EnvironmentalMeasurementApiPayload = {
      measurementEquipmentId: formData.measurementEquipmentId ?? null,
      // Vazio é a avaliação de periculosidade, que não mede grandeza nenhuma;
      // `Number('')` é 0 e faria o servidor recusar por valor não positivo (#424).
      value: numberOrNull(formData.value),
      unit: formData.unit || null,
      measuredAt: formData.measuredAt,
      technique: formData.technique || null,
      exposureTime: formData.exposureTime || null,
      responsibleUserId: formData.responsibleUserId ?? null,
      observations: formData.observations || null,
      exceedsLimit: formData.exceedsLimit,
      storedQuantity: numberOrNull(formData.storedQuantity),
      storedQuantityUnit: orNull(formData.storedQuantityUnit),
      transportedQuantity: numberOrNull(formData.transportedQuantity),
      transportedQuantityUnit: orNull(formData.transportedQuantityUnit),
      riskArea: orNull(formData.riskArea),
      storageType: orNull(formData.storageType),
      hazardousFraming: Boolean(formData.hazardousFraming),
      hazardousAnnex: orNull(formData.hazardousAnnex),
      unhealthyFraming: Boolean(formData.unhealthyFraming),
      unhealthyDegreeApplied: orNull(formData.unhealthyDegreeApplied),
      appliedToleranceLimit: orNull(formData.appliedToleranceLimit),
      appliedToleranceLimitSource: orNull(formData.appliedToleranceLimitSource),
      assessmentType: formData.assessmentType || null,
      comfortParameter: formData.comfortParameter || null,
      // O ponto medido só diz algo junto do parâmetro de conforto; sem ele, é resto de digitação.
      measurementPoint: formData.comfortParameter ? orNull(formData.measurementPoint) : null,
    };

    // Só o risco com fórmula tem % de quartzo; o servidor recusa o campo nos demais (#459).
    const quartzPercent = numberOrNull(formData.quartzPercent);
    if (quartzPercent !== null) {
      payload.quartzPercent = quartzPercent;
    }

    if (includeRiskApplication && formData.riskApplicationId) {
      payload.riskApplicationId = formData.riskApplicationId;
    }

    return payload;
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: EnvironmentalMeasurement): EnvironmentalMeasurementFormData {
    return {
      riskApplicationId: apiData.riskApplicationId,
      measurementEquipmentId: apiData.measurementEquipmentId,
      value: apiData.value === null || apiData.value === undefined ? '' : String(apiData.value),
      unit: apiData.unit ?? '',
      measuredAt: apiData.measuredAt ? apiData.measuredAt.slice(0, 10) : '',
      technique: apiData.technique ?? '',
      exposureTime: apiData.exposureTime ?? '',
      responsibleUserId: apiData.responsibleUserId,
      observations: apiData.observations ?? '',
      exceedsLimit: apiData.exceedsLimit,
      storedQuantity: apiData.storedQuantity == null ? '' : String(apiData.storedQuantity),
      storedQuantityUnit: apiData.storedQuantityUnit ?? '',
      transportedQuantity: apiData.transportedQuantity == null ? '' : String(apiData.transportedQuantity),
      transportedQuantityUnit: apiData.transportedQuantityUnit ?? '',
      riskArea: apiData.riskArea ?? '',
      storageType: apiData.storageType ?? '',
      appliedToleranceLimit: apiData.appliedToleranceLimit ?? '',
      appliedToleranceLimitSource: apiData.appliedToleranceLimitSource ?? '',
      quartzPercent: apiData.quartzPercent == null ? '' : String(apiData.quartzPercent),
      assessmentType: apiData.assessmentType ?? '',
      comfortParameter: apiData.comfortParameter ?? '',
      measurementPoint: apiData.measurementPoint ?? '',
      hazardousFraming: apiData.hazardousFraming ?? false,
      hazardousAnnex: apiData.hazardousAnnex ?? '',
      unhealthyFraming: apiData.unhealthyFraming ?? false,
      unhealthyDegreeApplied: apiData.unhealthyDegreeApplied ?? '',
    };
  }

  /**
   * Get paginated list of environmental measurements
   */
  async getEnvironmentalMeasurements(
    params: EnvironmentalMeasurementListParams = {},
  ): Promise<EnvironmentalMeasurementListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = this.buildFilterParams(filters);
    queryParams.append('page', String(page));
    queryParams.append('limit', String(limit));

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { measurements, total, page, limit, totalPages } }
    const backendData = response.data.data ?? response.data;

    return {
      measurements: backendData.measurements ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get a measurement by ID
   */
  async getEnvironmentalMeasurementById(id: string | number): Promise<EnvironmentalMeasurement> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data ?? response.data;
  }

  /**
   * Create a measurement
   */
  async createEnvironmentalMeasurement(
    data: EnvironmentalMeasurementFormData,
  ): Promise<EnvironmentalMeasurement> {
    const payload = this.formDataToApiPayload(data, true);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data ?? response.data;
  }

  /**
   * Update a measurement. A aplicação de risco não muda no update.
   */
  async updateEnvironmentalMeasurement(
    id: string | number,
    data: EnvironmentalMeasurementFormData,
  ): Promise<EnvironmentalMeasurement> {
    const payload = this.formDataToApiPayload(data, false);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data ?? response.data;
  }

  /**
   * Delete a measurement
   */
  async deleteEnvironmentalMeasurement(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Export the measurements to Excel, honouring the filters active on screen
   */
  async exportEnvironmentalMeasurements(
    filters: EnvironmentalMeasurementFilters = {},
  ): Promise<Blob> {
    const query = this.buildFilterParams(filters).toString();

    const response = await apiClient.get(`${this.baseUrl}/export${query ? `?${query}` : ''}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  /**
   * Get default form values
   */
  getDefaultFormValues(): EnvironmentalMeasurementFormData {
    const today = new Date().toISOString().split('T')[0];

    return {
      riskApplicationId: null,
      measurementEquipmentId: null,
      value: '',
      unit: '',
      measuredAt: today,
      technique: '',
      exposureTime: '',
      responsibleUserId: null,
      observations: '',
      exceedsLimit: false,
      storedQuantity: '',
      storedQuantityUnit: '',
      transportedQuantity: '',
      transportedQuantityUnit: '',
      riskArea: '',
      storageType: '',
      hazardousFraming: false,
      hazardousAnnex: '',
      unhealthyFraming: false,
      unhealthyDegreeApplied: '',
      appliedToleranceLimit: '',
      appliedToleranceLimitSource: '',
      assessmentType: '',
      comfortParameter: '',
      measurementPoint: '',
    };
  }
}

// Export singleton instance
export const environmentalMeasurementService = new EnvironmentalMeasurementService();
export default environmentalMeasurementService;
